import type { components } from "@roastery/api-client";
import { useApi } from "@roastery/auth";
import { useQueryClient } from "@tanstack/react-query";
import { useRegistryLookup } from "../../lib/registry";

export type TenantSettings = components["schemas"]["TenantSettings"];
export type TenantUser = components["schemas"]["TenantUser"];
// springdoc leaves record components optional in the schema, hence the NonNullable wrapper.
export type WeightUnit = NonNullable<TenantSettings["weightUnit"]>;

export const WEIGHT_UNITS: readonly WeightUnit[] = ["KG", "LB"];

const ME_PATH = "/api/v1/me";
const SETTINGS_PATH = "/api/v1/tenant/settings";

export function useTenantSettings() {
  const api = useApi();
  return api.query.useQuery("get", SETTINGS_PATH, {});
}

/**
 * The roastery's name, currency and units decide how every screen formats data, and they travel to the
 * client inside `/api/v1/me` as well — so a change has to invalidate both queries, not just this one.
 */
export function useUpdateTenantSettings() {
  const api = useApi();
  const queryClient = useQueryClient();
  return api.query.useMutation("patch", SETTINGS_PATH, {
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["get", SETTINGS_PATH] });
      void queryClient.invalidateQueries({ queryKey: ["get", ME_PATH] });
    },
  });
}

/** Editing one's own profile changes what `/api/v1/me` answers and how the user list reads. */
export function useUpdateProfile() {
  const api = useApi();
  const queryClient = useQueryClient();
  return api.query.useMutation("patch", ME_PATH, {
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["get", ME_PATH] });
      void queryClient.invalidateQueries({ queryKey: ["get", "/api/v1/tenant/users"] });
    },
  });
}

export type TenantLegal = NonNullable<TenantSettings["legal"]>;

/**
 * The roastery's logo (US-23.2). Not a generated mutation: the body is `FormData`, which
 * `openapi-fetch` passes through untouched so the browser sets the multipart boundary itself.
 */
export function useUploadLogo() {
  const api = useApi();
  const queryClient = useQueryClient();
  return async (file: File): Promise<TenantSettings> => {
    const form = new FormData();
    form.append("file", file);
    const { data, error, response } = await api.fetch.PUT("/api/v1/tenant/settings/logo", {
      body: form as never,
    });
    if (error || !data) {
      throw Object.assign(new Error("logo upload failed"), { response, error });
    }
    await queryClient.invalidateQueries({ queryKey: ["get", SETTINGS_PATH] });
    return data;
  };
}

export function useRemoveLogo() {
  const api = useApi();
  const queryClient = useQueryClient();
  return api.query.useMutation("delete", "/api/v1/tenant/settings/logo", {
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["get", SETTINGS_PATH] }),
  });
}

/**
 * The roastery's own registered details from the public register (US-40.4), behind
 * `tenant.settings.write`. Fills the legal name and the registered address; the footer sentence is
 * left alone, because the court and the registry number are something a roastery writes itself.
 */
export function useTenantRegistryLookup() {
  const api = useApi();
  return useRegistryLookup((query) =>
    api.fetch.GET("/api/v1/tenant/settings/registry-lookup", { params: { query } }),
  );
}
